import { StoreGroup } from '@/common/types/purchasing';
import { components, OptionProps } from 'react-select';

import SvgRender from '@/library/components/general/SvgRender';
import burger from '@/common/assets/svg/common/chapter.svg';
import emptyRibbon from '@/common/assets/svg/common/ribbon-line.svg';

type StoreGroupWithChildren = StoreGroup & { children: StoreGroupWithChildren[] };

type StoreGroupSorted = StoreGroup & { sort_index: number };

export function OptionWithParentIcon<Option extends StoreGroup>({
  children,
  ...props
}: OptionProps<Option>) {
  return (
    <components.Option className="d-flex align-items-center" {...props}>
      <SvgRender
        className={`me-1 ms-${props.data.depth && props.data.depth >= 1 ? props.data.depth + 1 : 0}`}
        src={props.data.parent_id ? emptyRibbon : burger}
        style={{ minWidth: 12, height: 12 }}
      />
      {`${props.data.code ? `${props.data.code}. ` : ''} ${props.data.name}`}
    </components.Option>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildAndSortStoreGroupsTree(groups: StoreGroup[]): StoreGroupSorted[] {
  const groupMap = new Map<number, StoreGroupWithChildren>();
  const rootGroups: StoreGroupWithChildren[] = [];

  groups.forEach(group => {
    groupMap.set(group.id, { ...group, children: [] });
  });

  groups.forEach(group => {
    const node = groupMap.get(group.id)!;
    if (group.parent_id === null) {
      rootGroups.push(node);
    } else {
      const parent = groupMap.get(group.parent_id);
      if (parent) {
        parent.children!.push(node);
      }
    }
  });

  function sortTree(nodes: StoreGroupWithChildren[]): StoreGroupWithChildren[] {
    return nodes
      .sort((a, b) => {
        if (a.code === null && b.code !== null) return 1;
        if (a.code !== null && b.code === null) return -1;
        if (a.code !== null && b.code !== null) {
          const codeComparison = a.code.localeCompare(b.code);
          if (codeComparison !== 0) return codeComparison;
        }
        return a.name.localeCompare(b.name);
      })
      .map(node => ({
        ...node,
        children: sortTree(node.children!)
      }));
  }

  function flattenTree(
    nodes: StoreGroupWithChildren[],
    result: StoreGroupSorted[] = []
  ): StoreGroupSorted[] {
    for (const node of nodes) {
      const { children, ...flatNode } = node;

      result.push({ ...flatNode, sort_index: result.length }); // Add current node
      flattenTree(children!, result); // Recursively add children
    }
    return result;
  }

  return flattenTree(sortTree(rootGroups));
}
